import * as ToastPrimitive from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ElementRef, Ref } from "react";
import { toastSwipeUtilities } from "@/components/ui/radix-utils";
import { transitions } from "@/styles/transitions";
import { cn } from "@/utils/cn";

// CVA variants for Toast components
const toastVariants = cva(
  [
    "group relative flex w-full items-center justify-between rounded-md border p-4 pr-8 shadow-md",
    "transition-standard radix-state-open:animate-in radix-state-closed:animate-out",
    "data-[swipe=end]:animate-out radix-state-closed:fade-out-80",
    "radix-state-open:slide-in-from-bottom-full radix-state-closed:slide-out-to-right-full",
    "data-[swipe=cancel]:translate-x-0",
    "bg-opacity-80 backdrop-blur-sm",
  ],
  {
    defaultVariants: {
      variant: "default",
    },
    variants: {
      variant: {
        default:
          "bg-white border-gray-200 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-50",
        error:
          "bg-red-50 border-red-200 text-red-900 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300",
        info: "bg-theme-primary/10 border-theme-primary/20 text-theme-primary dark:bg-theme-primary/20 dark:border-theme-primary/30 dark:text-theme-primary",
        success:
          "bg-green-50 border-green-200 text-green-900 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300",
        warning:
          "bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300",
      },
    },
  },
);

export interface ToastProps
  extends ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {
  ref?: Ref<ElementRef<typeof ToastPrimitive.Root>>;
}

/**
 * Toast provider component that must wrap the application
 */
const ToastProvider = ToastPrimitive.Provider;

interface ToastViewportProps
  extends ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport> {
  ref?: Ref<ElementRef<typeof ToastPrimitive.Viewport>>;
}

/**
 * Toast viewport component defines where toasts will appear
 */
const ToastViewport = ({ className, ref, ...props }: ToastViewportProps) => (
  <ToastPrimitive.Viewport
    className={cn(
      "fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 gap-3",
      "sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col-reverse md:max-w-[420px]",
      className,
    )}
    ref={ref}
    {...props}
  />
);

/**
 * Toast component for displaying notifications
 */
const Toast = ({ className, variant, ref, ...props }: ToastProps) => {
  return (
    <ToastPrimitive.Root
      className={cn(
        toastVariants({ variant }),
        toastSwipeUtilities.swipeMoveX,
        toastSwipeUtilities.swipeEndX,
        className,
      )}
      ref={ref}
      {...props}
    />
  );
};

interface ToastActionProps
  extends ComponentPropsWithoutRef<typeof ToastPrimitive.Action> {
  ref?: Ref<ElementRef<typeof ToastPrimitive.Action>>;
}

/**
 * Toast action component for action button
 */
const ToastAction = ({ className, ref, ...props }: ToastActionProps) => (
  <ToastPrimitive.Action
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border px-3 text-sm font-medium",
      transitions.colors,
      "border-gray-200 bg-transparent hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400",
      "dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-gray-600",
      "group-[.success]:border-green-200 group-[.success]:dark:border-green-700",
      "group-[.success]:hover:bg-green-100 group-[.success]:dark:hover:bg-green-900/30",
      "group-[.info]:border-theme-primary/20 group-[.info]:dark:border-theme-primary/40",
      "group-[.info]:hover:bg-theme-primary/10 group-[.info]:dark:hover:bg-theme-primary/20",
      "group-[.warning]:border-amber-200 group-[.warning]:dark:border-amber-700",
      "group-[.warning]:hover:bg-amber-100 group-[.warning]:dark:hover:bg-amber-900/30",
      "group-[.error]:border-red-200 group-[.error]:dark:border-red-700",
      "group-[.error]:hover:bg-red-100 group-[.error]:dark:hover:bg-red-900/30",
      className,
    )}
    ref={ref}
    {...props}
  />
);

interface ToastCloseProps
  extends ComponentPropsWithoutRef<typeof ToastPrimitive.Close> {
  ref?: Ref<ElementRef<typeof ToastPrimitive.Close>>;
}

/**
 * Toast close button component
 */
const ToastClose = ({ className, ref, ...props }: ToastCloseProps) => (
  <ToastPrimitive.Close
    className={cn(
      "absolute right-1 top-1 rounded-md p-1 opacity-70 transition-opacity-standard",
      "hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-1",
      "group-[.default]:text-gray-500 group-[.default]:hover:text-gray-900 group-[.default]:focus:ring-gray-400",
      "group-[.default]:dark:text-gray-400 group-[.default]:dark:hover:text-gray-50 group-[.default]:dark:focus:ring-gray-600",
      "group-[.success]:text-green-700 group-[.success]:hover:text-green-900 group-[.success]:focus:ring-green-400",
      "group-[.success]:dark:text-green-400 group-[.success]:dark:hover:text-green-50 group-[.success]:dark:focus:ring-green-600",
      "group-[.info]:text-theme-primary group-[.info]:hover:text-theme-secondary group-[.info]:focus:ring-theme-primary/50",
      "group-[.info]:dark:text-theme-primary group-[.info]:dark:hover:text-theme-secondary group-[.info]:dark:focus:ring-theme-primary/50",
      "group-[.warning]:text-amber-700 group-[.warning]:hover:text-amber-900 group-[.warning]:focus:ring-amber-400",
      "group-[.warning]:dark:text-amber-400 group-[.warning]:dark:hover:text-amber-50 group-[.warning]:dark:focus:ring-amber-600",
      "group-[.error]:text-red-700 group-[.error]:hover:text-red-900 group-[.error]:focus:ring-red-400",
      "group-[.error]:dark:text-red-400 group-[.error]:dark:hover:text-red-50 group-[.error]:dark:focus:ring-red-600",
      className,
    )}
    ref={ref}
    toast-close=""
    {...props}
  >
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      height="16"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Close</title>
      <line x1="18" x2="6" y1="6" y2="18" />
      <line x1="6" x2="18" y1="6" y2="18" />
    </svg>
  </ToastPrimitive.Close>
);

interface ToastTitleProps
  extends ComponentPropsWithoutRef<typeof ToastPrimitive.Title> {
  ref?: Ref<ElementRef<typeof ToastPrimitive.Title>>;
}

/**
 * Toast title component
 */
const ToastTitle = ({ className, ref, ...props }: ToastTitleProps) => (
  <ToastPrimitive.Title
    className={cn("text-sm font-semibold", className)}
    ref={ref}
    {...props}
  />
);

interface ToastDescriptionProps
  extends ComponentPropsWithoutRef<typeof ToastPrimitive.Description> {
  ref?: Ref<ElementRef<typeof ToastPrimitive.Description>>;
}

/**
 * Toast description component
 */
const ToastDescription = ({
  className,
  ref,
  ...props
}: ToastDescriptionProps) => (
  <ToastPrimitive.Description
    className={cn("text-sm opacity-90", className)}
    ref={ref}
    {...props}
  />
);

export {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
};
